import { useState, useEffect, useCallback } from "react";
import { useFaust } from "../../contexts/FaustContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Copy, Check } from "lucide-react";

type ShareDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function ShareDialog({ open, onOpenChange }: ShareDialogProps) {
    const { editorRef, compileOptionsRef } = useFaust();
    const [autorun, setAutorun] = useState(true);
    const [shareUrl, setShareUrl] = useState("");
    const [copied, setCopied] = useState(false);

    const generateUrl = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) return "";
        const base = window.location.origin + window.location.pathname;
        const urlParams = new URLSearchParams();
        urlParams.set("autorun", autorun ? "1" : "0");
        urlParams.set("voices", String(compileOptionsRef.current.voices));
        urlParams.set("name", "untitled");
        urlParams.set(
            "inline",
            btoa(editor.getValue()).replace(/\+/g, "-").replace(/\//g, "_")
        );
        return base + "?" + urlParams.toString();
    }, [editorRef, compileOptionsRef, autorun]);

    useEffect(() => {
        if (open) {
            setShareUrl(generateUrl());
            setCopied(false);
        }
    }, [open, generateUrl]);

    useEffect(() => {
        setShareUrl(generateUrl());
        setCopied(false);
    }, [autorun, generateUrl]);

    const handleCopy = useCallback(() => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl);
        } else {
            const input = document.querySelector<HTMLInputElement>("#share-url-input");
            if (input) {
                input.focus();
                input.select();
                document.execCommand("copy");
            }
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [shareUrl]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md bg-[#1f1f1f] border-white/[0.08]">
                <DialogHeader>
                    <DialogTitle className="text-zinc-100 font-display">
                        Share your code
                    </DialogTitle>
                </DialogHeader>

                <div className="flex items-center gap-2 py-2">
                    <Checkbox
                        id="autorun"
                        checked={autorun}
                        onCheckedChange={(v) => setAutorun(!!v)}
                    />
                    <Label htmlFor="autorun" className="text-sm text-zinc-400 cursor-pointer">
                        Autorun your code
                    </Label>
                </div>

                <DialogFooter className="flex-row gap-2 sm:flex-row">
                    <Input
                        id="share-url-input"
                        value={shareUrl}
                        readOnly
                        className="flex-1 text-xs bg-black border-white/[0.08] font-mono"
                    />
                    <Button
                        variant="outline"
                        className={`gap-2 shrink-0 ${
                            copied
                                ? "border-green-700 text-green-400"
                                : "border-[#0078d4] text-[#40a6ff] hover:bg-[#0078d4]/10"
                        }`}
                        onClick={handleCopy}
                    >
                        {copied ? (
                            <>
                                <Check className="h-4 w-4" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy className="h-4 w-4" />
                                Copy
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
